/**
 * ShortLinkRedirect — resolves a short manage-link code from an SMS
 * (`/m/:code`) into the signed token, then hands off to the existing
 * ManageBookingPage (`/manage-booking?token=...`).
 *
 * The SMS only ever carries the short `/m/{code}` URL; the long signed
 * token never appears in the text. Security is unchanged — the backend
 * still verifies the HMAC token on every manage action.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShortLinkRedirect() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // Pass a supported action through to the manage page (e.g. a reminder's
  // reschedule deep link: /m/{code}?a=reschedule). NAV-ONLY — the manage
  // page only pre-opens the existing confirm dialog.
  const action = searchParams.get("a") || searchParams.get("action");

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      setError("This link is incomplete. Please open it again from your text message.");
      return;
    }
    fetch(`${BACKEND_URL}/api/booking/short/${encodeURIComponent(code)}`)
      .then((r) => {
        if (!r.ok) throw new Error("not_found");
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.token) {
          let dest = `/manage-booking?token=${encodeURIComponent(data.token)}`;
          if (action === "reschedule") {
            dest += `&action=reschedule`;
          }
          navigate(dest, { replace: true });
        } else {
          setError("We couldn't open that appointment. Please call the salon and we'll help.");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("This link is invalid or has expired. Please call the salon and we'll help.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, action, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#F7F3EE" }}
      data-testid="short-link-redirect"
    >
      <div className="text-center max-w-sm">
        {error ? (
          <p className="text-sm" style={{ color: "#8A4B2A" }} data-testid="short-link-error">
            {error}
          </p>
        ) : (
          <p className="text-sm" style={{ color: "#5B5750" }} data-testid="short-link-loading">
            Opening your appointment…
          </p>
        )}
      </div>
    </div>
  );
}
